<script setup lang="ts">


import {reactive, ref} from "vue";
import {useReCaptcha} from "vue-recaptcha-v3";
import {VueSpinner} from "vue3-spinners";
import * as CoreService from "@/services/core.service";
import {useVuelidate} from '@vuelidate/core'
import {required, email} from '@vuelidate/validators'
import {RouterLink} from "vue-router";
import {event} from 'vue-gtag';

const {executeRecaptcha, recaptchaLoaded} = useReCaptcha()

const state = ref('idle')
const data = reactive({contact: {email: ''}})
const loading = ref<boolean>(false)

const rules = {
  contact: {
    email: {required, email, $autoDirty: true}
  }
}
const $v = useVuelidate(rules, data)
const subscribe = async () => {

  if ($v.value.$invalid) {
    return
  }

  loading.value = true
  await recaptchaLoaded()
  await executeRecaptcha('login')
  state.value = 'idle'

  loading.value = false
  state.value = 'submit'

  CoreService.subscribe(data.contact.email)
      .then(() => {
        state.value = 'submit'

      })
      .catch(() => {
        state.value = 'error'

      })
      .finally(() => {
        loading.value = false
      })
}


const partners = [
  {
    id: "pantaenius",
    link: '/partners/pantaenius',
    image: new URL("@/assets/home/footer_partners/pantaenius.svg", import.meta.url).href,
  },
  {
    id: "EYA",
    link: '/partners/EYA',
    image: new URL("@/assets/home/footer_partners/2025-05-05-EYA-UPDATED-LOGO.svg", import.meta.url).href,
  },
  {
    id: "allianz",
    link: '/partners/alianz',
    image: new URL("@/assets/home/footer_partners/allianz.svg", import.meta.url).href,
  },
  {
    id: "IYBA",
    link: '/partners/IYBA',
    image: new URL("@/assets/home/footer_partners/IYBA.svg", import.meta.url).href,
  },
  {
    id: "britishmarine",
    link: '/partners/britishmarine',
    image: new URL("@/assets/home/footer_partners/british-marine.svg", import.meta.url).href,
  },
];

const otherPartners = [
  {
    id: "yachtpool",
    link: '/partners/yachtpool',
    image: new URL("@/assets/partners/page/yachtpool_2025-03-10.png", import.meta.url).href,
  },
  {
    id: "sealogy",
    link: '/partners/sealogy',
    image: new URL("@/assets/partners/page/sealogy.png", import.meta.url).href,
  },
]


</script>
<template>
  <div class="container pt-24 blueSection">
    <div
        class="bg-primary-blue bluesection-box rounded-8xl flex sm:justify-center md:justify-center lg:justify-center xl:justify-start text-white h-64">
      <div class="flex justify-between align-center w-[85%] bluesection-content gap-x-6" v-if="state != 'submit'">
        <div class="flex w-1/3 seagull-adjust">
          <img
              src="@/assets/icons/svg/seagull.svg"
              class="object-cover min-w-[30px] max-w-[150px] md:max-w-[150px] lg:max-w-[150px] h-auto"
              alt="Sea gull"
          />
        </div>

        <div class="align-center w-2/3 bluesection-email__input">
          <div class="text-bw-white blue">
            <p class="bluesection-title font-bold responsive-title-4">
              {{ $t('stayInformed') }}
            </p>
            <p class="bluesection-title responsive-body-small pt-2">
              {{ $t('getDeals') }}
            </p>
            <p class="bluesection-title responsive-body-small pb-3">
              {{ $t('saveMoney') }}
            </p>
          </div>
          <div class="flex align-center w-full gap-x-6 bluesection-email__input">
            <div class="bg-white flex rounded-lg w-[55%] bluesection-email__input-field">

              <input
                  v-model="data.contact.email"
                  required
                  :disabled="loading"
                  type="email"
                  class="emailSub bg-gradient-to-r from-gradients-ghostWhite md:w-full p-4 rounded-8xl placeholder-bw-black"
                  :class="{'to-gradients-red placeholder-red-300': $v.$error, ' to-gradients-aliceBlue': $v.$error}"
                  :placeholder="$t('enterEmailPlaceholder')"
              />
              <div class="p-4 emailSub flex flex-col justify-center">
                <div class="w-fit px-5 py-2 rounded-22xl shadow-md transition transform hover:scale-110 cursor-pointer"
                     v-if="!loading"
                     @click="subscribe"
                     :class="{'bg-notifications-cancelled ':state =='error', 'bg-primary-blue':state !='error'}"
                >
                  <img src="@/assets/icons/svg/Arrow_right_black_16x16.svg" alt="arrow"/>
                </div>
                <div class="w-fit px-5 py-2 rounded-22xl"
                     v-if="loading"
                >
                  <VueSpinner size="16"
                              color="#1B84FF"
                  />
                </div>
              </div>
            </div>
            <div class="sub w-[38%]">
              <p class="responsive-body-xsmall">
                {{ $t('subscribeMessage') }}
                <RouterLink
                    class="underline text-white hover:text-white"
                    :to="`/${$i18n.locale}/privacy`"
                >
                  {{ $t('privacyPolicyTitle') }}
                </RouterLink>
              </p>
            </div>
          </div>
        </div>
      </div>
      <Transition>
        <div class="flex justify-between align-center w-[85%] bluesection-content" v-if="state == 'submit'">
          <p class="bluesection-title text-white responsive-title-2 flex-1 text-center">
            Thank you for subscribing!
          </p>
        </div>
      </Transition>
    </div>
  </div>

  <footer class="container pt-16 pb-8 flex flex-col">
    <div class="flex footer-menu justify-between lg:gap-x-52">
      <div class="mainContainer flex flex-col justify-between mb-5 ">

        <div>
          <div class="mb-6 text-sm">
            <div class="logo flex items-center mb-10">
              <img
                  src="/assets/images/logo_black_35_years.png"
                  alt="Cosmos Yachting"
                  class="h-9 sm:h-10 w-auto max-w-full object-contain"
              />
            </div>
            <div class="flex items-center gap-2">
              <p class="responsive-body-xsmall font-semibold">GER</p>
              <div class="flex items-center gap-2 text-gray-400 text-no-wrap">
                <a
                    href="tel:+4989411471900"
                    class="responsive-body-small text-bw-darkGrey hover:text-black">+49 (0)89 411 47 19 00
                </a>
              </div>
            </div>

            <div class="flex items-center gap-4">
              <p class="responsive-body-xsmall font-semibold">UK</p>
              <div class="flex items-center gap-2 text-gray-400 text-no-wrap">
                <a
                    href="tel:+442088780880"
                    class="responsive-body-small text-bw-darkGrey hover:text-black">+44 (0)20 8878 0880
                </a>
              </div>
            </div>

            <div class="flex items-center space-x-6 mt-10">
              <a href="https://www.instagram.com/cosmosyachting/" target="_blank">
                <img src="/assets/images/instagram.png" alt="Instagram" class="h-6 min-w-6"/>
              </a>
              <a href="https://www.facebook.com/cosmosyachtingHQ/" target="_blank">
                <img src="/assets/images/facebook.png" alt="Facebook" class="h-6 min-w-6"/>
              </a>
              <a href="https://www.youtube.com/channel/UCoiQh9QtoeQl_LveY-1rREA" target="_blank">
                <img src="/assets/images/youtube.png" alt="Youtube" class="h-6 min-w-6"/>
              </a>
              <a href="https://www.linkedin.com/company/cosmos-yachting-worldwide/" target="_blank">
                <img src="/assets/images/linkedin.png" alt="LinkedIn" class="h-6 min-w-6"/>
              </a>
            </div>

          </div>

        </div>
      </div>
      <div class="max-w-screen-xl grid grid-cols-2 md:grid-cols-3 gap-8 md:gap-17 links">
        <div class="flex flex-col">
          <span class="footer-menu__headers  responsive-body-large font-semibold mb-4">{{ $t('supportTitle') }}</span>
          <RouterLink class="mb-4  responsive-body-small font-semibold text-bw-darkGrey hover:text-black"
                      :to="`/${$i18n.locale}/inquiry`">
            {{ $t('inquiry') }}
          </RouterLink>
          <RouterLink class="mb-4  responsive-body-small font-semibold text-bw-darkGrey hover:text-black"
                      :to="`/${$i18n.locale}/help`">
            {{ $t('help') }}
          </RouterLink>
          <RouterLink class="mb-4 responsive-body-small font-semibold text-bw-darkGrey hover:text-black"
                      :to="`/${$i18n.locale}/faq`">
            {{ $t('faqs') }}
          </RouterLink>
        </div>

        <div class="flex flex-col">
          <span class="footer-menu__headers  responsive-body-large font-semibold mb-4">Cosmos</span>
          <RouterLink class="mb-4  responsive-body-small font-semibold text-bw-darkGrey hover:text-black"
                      :to="`/${$i18n.locale}/about`">
            {{ $t('aboutUs') }}
          </RouterLink>
          <RouterLink class="mb-4 responsive-body-small font-semibold text-bw-darkGrey hover:text-black"
                      :to="`/${$i18n.locale}/why-book`">
            {{ $t('whyBookWithUs') }}
          </RouterLink>
          <RouterLink class="mb-4 responsive-body-small tracking font-semibold text-bw-darkGrey hover:text-black"
                      :to="`/${$i18n.locale}/blog`">
            {{ $t('travelInspirations') }}
          </RouterLink>

        </div>
        <div class="legal flex flex-col">
          <div class="footer-menu__headers  responsive-body-large font-semibold mb-4">{{ $t('legal') }}</div>
          <RouterLink class="mb-4  responsive-body-small font-semibold text-bw-darkGrey hover:text-black"
                      :to="`/${$i18n.locale}/terms`">
            {{ $t('termsConditions') }}
          </RouterLink>
          <RouterLink class="mb-4  responsive-body-small font-semibold text-bw-darkGrey hover:text-black"
                      :to="`/${$i18n.locale}/privacy`">
            {{ $t('privacyStatement') }}
          </RouterLink>
          <RouterLink class="mb-4  responsive-body-small font-semibold text-bw-darkGrey hover:text-black"
                      :to="`/${$i18n.locale}/imprint`">
            {{ $t('imprint') }}
          </RouterLink>
        </div>

      </div>

    </div>


    <div class="flex flex-col md:flex-row justify-between w-full">

      <!-- Left: Logos -->
      <div class="grid partner-logos grid-cols-3 gap-4 justify-items-start md:justify-items-center  md:grid-cols-6 mb-10">
        <div
            v-for="(partner, index) in partners"
            :key="partner.id"
            :id="partner.id"
            :class="[
                  'flex items-end',
                  (index === partners.length ? 'col-span-2 sm:col-span-1 ' : '')
            ]"
        >
          <RouterLink :to="`/${$i18n.locale}${partner.link}`">
            <img
                :src="partner.image"
                :alt="partner.id"
                class="max-w-sm object-scale-down items-center"

            />
          </RouterLink>
        </div>
      </div>

      <!-- Right: Copyright -->
      <div>
        <div class="flex flex-col">
          <div>
            <div class="border-b pb-2 gap-1 flex align-end">
              <img
                  data-src="/assets/images/payment/powered-by-stripe.svg" alt="Stripe"
                  class="mt-2 mr-2 ls-is-cached lazyloaded creditCards"
                  src="/assets/images/payment/powered-by-stripe.svg">
              <div class="responsive-body-xsmall">{{ $t('securePayment') }}</div>
            </div>
            <hr/>
            <div class="flex flex-justify gap-x-4 mt-2 mb-10 md:mb-4">
              <img data-src="/assets/images/payment/apple-pay-logo-mono.svg"
                   height="25"
                   alt="Apple Pay" class="h-6  flex-col"
                   src="/assets/images/payment/apple-pay-logo-mono.svg">

              <img
                  data-src="/assets/images/payment/gpay-logo-mono.svg" height="16"
                  alt="Gpay" class="h-6  flex-col"
                  src="/assets/images/payment/gpay-logo-mono.svg">
              <img
                  data-src="/assets/images/payment/visa-logo-mono.svg" height="25"
                  alt="Visa" class="h-6  flex-col"
                  src="/assets/images/payment/visa-logo-mono.svg">
              <img
                  data-src="/assets/images/payment/mastercard-logo-mono.svg" height="25"
                  alt="Mastercard" class="h-6  flex-col"
                  src="/assets/images/payment/mastercard-logo-mono.svg">
              <img
                  data-src="/assets/images/payment/bank-transfer-logo-mono.svg" height="25"
                  alt="Bank transfer" class="h-6  flex-col"
                  src="/assets/images/payment/bank-transfer-logo-mono.svg">
              <img
                  data-src="/assets/images/payment/secure-global-sign-mono.svg" height="25"
                  alt="SSL Secure" class="h-6  flex-col"
                  src="/assets/images/payment/secure-global-sign-mono.svg">
            </div>
          </div>
        </div>

        <p class="text-bw-darkGrey font-normal responsive-body-xsmall text-left md:text-right leading-tight md:mt-0">
          This site is protected by reCAPTCHA and the Google
          <a href="https://policies.google.com/privacy">Privacy Policy</a> and
          <a href="https://policies.google.com/terms">Terms of Service</a> apply.<br/>
          <span class="block sm:hidden">© 1988 - 2025 Cosmos Yachting.</span>
          <span class="block sm:hidden">{{ $t('allRightsReserved') }}</span>
          <!-- For larger screens -->
          <span class="hidden sm:inline">© 1988 - 2025 Cosmos Yachting. {{ $t('allRightsReserved') }}</span>
        </p>
      </div>

    </div>
  </footer>
</template>


<style scoped>
.emailSub img {
  max-width: unset;
}


@media (min-width: 911px) {
  .partner-logos img {
    max-width: 100%; /* Ensure the logos scale correctly */
    height: auto;
  }
}

@media (max-width: 910px) {
  .partner-logos img {
    max-width: 100%; /* Ensure the logos scale correctly */
    height: auto;
  }
}


@media (max-width: 1050px) {

  .blueSection .bluesection-content {
    flex-direction: column;
    width: 85%;
    height: 100%;
    justify-content: center;
    row-gap: 0.85rem;
  }

  .bluesection-title {

    width: 100%;
  }

  .blueSection .bluesection-box {
    height: 25rem;
  }


  .blueSection .bluesection-email__input {
    flex-direction: column;
    align-items: start !important;
    width: 100%;
    row-gap: 1rem;
  }

  .blueSection .bluesection-email__input input {
    width: 100%;
  }

  .blueSection .bluesection-email__input .bluesection-email__input-field {
    width: 100%;
  }

  .seagull-adjust {
    align-self: flex-start !important;
  }
}

@media (max-width: 1050px) and (min-width: 640px) {
  .sub {
    width: 85%;
  }

  .blueSection .bluesection-email__input .bluesection-email__input-field {
    width: 75%;
  }
}

@media (max-width: 1480px) and (min-width: 1051px) {
  /**********/
  .blueSection .bluesection-email__input .bluesection-email__input-field {
    width: 43%;
  }

  .blue {
    width: 43%;
  }

  .seagull-adjust {
    margin-left: 5%;
  }

}


@media (max-width: 1600px) and (min-width: 1481px) {
  /**********/
  .blueSection .bluesection-email__input .bluesection-email__input-field {
    width: 43%;
  }

  .blue {
    width: 43%;
  }
}


@media (max-width: 1780px) and (min-width: 1601px) {
  /**********/
  .blueSection .bluesection-email__input .bluesection-email__input-field {
    width: 43%;
    margin-left: -1%;
  }

  .blue {
    width: 43%;
    margin-left: -1%;
  }

  .seagull-adjust {
    margin-left: -1%;
  }
}

@media (max-width: 1919px) and (min-width: 1781px) {
  /**********/
  .blueSection .bluesection-email__input .bluesection-email__input-field {
    width: 48%;
    margin-left: -5%;
  }

  .blue {
    width: 43%;
    margin-left: -5%;
  }

  .seagull-adjust {
    margin-left: -5%;
  }
}

@media (min-width: 1920px) {
  /**********/
  .blueSection .bluesection-email__input .bluesection-email__input-field {
    width: 55%;
  }

  .blue {
    width: 43%;
  }

  .seagull-adjust {
    margin-left: 3%;
  }
}

@media (min-width: 1960px) {
  /**********/
  .blueSection .bluesection-email__input .bluesection-email__input-field {
    width: 55%;
    margin-left: -0.5%;
  }

  .blue {
    width: 43%;
    margin-left: -0.5%;
  }

  .seagull-adjust {
    margin-left: -0.5%;
  }
}

@media (max-width: 1024px) and (min-width: 911px) {
  .links {
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
  }
}

@media (max-width: 910px) {
  .links {
    padding-top: 20px;
    padding-bottom: 20px;
    padding-right: 0 !important;
    padding-left: 0 !important;
    margin: unset !important;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .footer-menu {
    flex-direction: column;
  }

  .payments-desktop {
    display: none;
  }

  .payments-mobile {
    display: flex;
  }
}

@media (min-width: 640px) {
  .creditCards {
    height: 28px;
  }
}

@media (max-width: 640px) {

  footer {
    flex-direction: column;
  }

  .creditCards {
    height: 24px;
  }

  .sub {
    width: fit-content;
  }

  .sub p {
    line-height: 1.25;
  }

  .emailSub {
    height: 64px;
  }
}

@media (max-width: 768px) {
  /**********/
  .bluesection-box {
    justify-content: center;
  }
}

@media (max-width: 580px) {
  .blueSection {
    padding-top: 2.75rem !important;
  }
}


@media (min-width: 1051px) {
  /**********/
  .seagull-adjust {
    justify-content: flex-end !important;

  }
}

/* we will explain what these classes do next! */
.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
